// src/pages/Appointments.jsx
import React, { useState, useEffect } from 'react';
import { Header } from '../components';
import {  FiFilter, FiUser, FiMapPin, FiClock, FiSearch, FiChevronLeft, FiChevronRight, FiX } from 'react-icons/fi';
import axios from 'axios';
import { useStateContext } from '../contexts/ContextProvider';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import DatePicker from 'react-datepicker/dist/react-datepicker';
import "react-datepicker/dist/react-datepicker.css";

// Configure localizer for the calendar
const localizer = momentLocalizer(moment);

// Status badge component
const StatusBadge = ({ status }) => {
  const getStatusColor = () => {
    switch (status) {
      case 'confirmed':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'completed':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'cancelled':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor()}`}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
};

// Payment status badge component
const PaymentBadge = ({ status }) => {
  const getStatusColor = () => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'refunded':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'failed':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor()}`}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
};

const formatAmount = (value) => `₹${Number(value || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

const sumPrices = (items = []) => items.reduce((sum, item) => sum + (Number(item.price) || 0), 0);

const BreakupRow = ({ label, value, sign, className = '' }) => (
  <div className={`flex justify-between py-1 text-sm ${className}`}>
    <span>{label}</span>
    <span>{sign}{formatAmount(value)}</span>
  </div>
);

// Payment breakup for an appointment
const PaymentBreakup = ({ appointment }) => {
  const servicesTotal = sumPrices(appointment.selectedServices);
  const packagesTotal = sumPrices(appointment.selectedPackages);
  const addonsTotal = sumPrices(appointment.selectedAddons);
  const itemsTotal = servicesTotal + packagesTotal + addonsTotal;

  const couponDiscount = Number(appointment.couponDiscount) || 0;
  const membershipDiscount = Number(appointment.membershipDiscount) || 0;
  const subsidyUsed = Number(appointment.subsidyUsed) || 0;
  const convenienceFees = Number(appointment.convenienceFees) || 0;
  const totalAmount = Number(appointment.totalAmount) || 0;

  // Whatever the stored fields don't explain, so the breakup always adds up to the total
  const adjustment = totalAmount - (itemsTotal - couponDiscount - membershipDiscount - subsidyUsed + convenienceFees);
  const showAdjustment = Math.abs(adjustment) >= 1;

  const commissionAmount = Number(appointment.commissionAmount) || 0;
  const vendorEarnings = Number(appointment.vendorEarnings) || 0;
  const refundAmount = Number(appointment.refundAmount) || 0;
  const cancellationCharge = Number(appointment.cancellationCharge) || 0;

  return (
    <div className="mb-4">
      <h4 className="text-sm font-medium text-gray-500 mb-2">Payment Breakup</h4>
      <div className="bg-gray-50 rounded-md p-3 text-gray-700">
        {servicesTotal > 0 && <BreakupRow label="Services" value={servicesTotal} />}
        {packagesTotal > 0 && <BreakupRow label="Packages" value={packagesTotal} />}
        {addonsTotal > 0 && <BreakupRow label="Add-ons" value={addonsTotal} />}
        <BreakupRow label="Item Total" value={itemsTotal} className="font-medium border-t border-gray-200 mt-1 pt-2" />

        {couponDiscount > 0 && (
          <BreakupRow
            label={`Coupon Discount${appointment.couponCode ? ` (${appointment.couponCode})` : ''} · ${appointment.couponFundedBy === 'vendor' ? 'vendor-funded' : 'platform-funded'}`}
            value={couponDiscount}
            sign="− "
            className="text-green-600"
          />
        )}
        {membershipDiscount > 0 && (
          <BreakupRow label="Membership Discount" value={membershipDiscount} sign="− " className="text-green-600" />
        )}
        {subsidyUsed > 0 && (
          <BreakupRow label="Membership Subsidy Used" value={subsidyUsed} sign="− " className="text-green-600" />
        )}
        {convenienceFees > 0 && (
          <BreakupRow
            label={`Convenience Fee${appointment.convenienceFeesPercentage ? ` (${appointment.convenienceFeesPercentage}%)` : ''}`}
            value={convenienceFees}
            sign="+ "
          />
        )}
        {showAdjustment && (
          <BreakupRow
            label={adjustment < 0 ? 'Other Discount' : 'Other Charges'}
            value={Math.abs(adjustment)}
            sign={adjustment < 0 ? '− ' : '+ '}
            className={adjustment < 0 ? 'text-green-600' : ''}
          />
        )}

        <div className="flex justify-between pt-2 mt-1 border-t border-gray-200">
          <span className="text-base font-medium text-gray-900">Total Amount</span>
          <span className="text-lg font-bold text-gray-900">{formatAmount(totalAmount)}</span>
        </div>

        {(commissionAmount > 0 || vendorEarnings > 0) && (
          <div className="mt-3 pt-2 border-t border-dashed border-gray-300">
            <BreakupRow
              label={`Platform Commission${appointment.commissionPercentage ? ` (${appointment.commissionPercentage}%)` : ''}`}
              value={commissionAmount}
            />
            <BreakupRow label="Vendor Earnings" value={vendorEarnings} />
          </div>
        )}

        {(cancellationCharge > 0 || refundAmount > 0) && (
          <div className="mt-3 pt-2 border-t border-dashed border-gray-300">
            {cancellationCharge > 0 && (
              <BreakupRow
                label={`Cancellation Charge${appointment.cancellationChargePercentage ? ` (${appointment.cancellationChargePercentage}%)` : ''}`}
                value={cancellationCharge}
                className="text-red-600"
              />
            )}
            {refundAmount > 0 && <BreakupRow label="Refund Amount" value={refundAmount} />}
          </div>
        )}
      </div>
    </div>
  );
};

// Appointment detail modal
const AppointmentModal = ({ appointment, onClose, onStatusUpdate }) => {
  const [updating, setUpdating] = useState(false);
  const [showCancelInput, setShowCancelInput] = useState(false);
  const [cancellationReason, setCancellationReason] = useState('');
  const [statusMessage, setStatusMessage] = useState(null);

  if (!appointment) return null;

  const formatTime = (timeString) => {
    return moment(timeString, ['h:mm A', 'hh:mm A', 'HH:mm'], true).format('h:mm A');
  };

  const canUpdateStatus = !['completed', 'cancelled', 'no-show'].includes(appointment.status);

  const handleStatusUpdate = async (newStatus) => {
    if (newStatus === 'cancelled' && !cancellationReason.trim()) {
      setStatusMessage({ type: 'error', text: 'Please provide a cancellation reason.' });
      return;
    }

    setUpdating(true);
    setStatusMessage(null);

    try {
      const token = localStorage.getItem('token');
      const body = { status: newStatus };
      if (newStatus === 'cancelled') {
        body.cancellationReason = cancellationReason;
      }

      const response = await axios.patch(
        `${process.env.REACT_APP_API_URL}/api/admin/booking/${appointment._id}/status`,
        body,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (response.data.success) {
        setStatusMessage({ type: 'success', text: response.data.message });
        setShowCancelInput(false);
        setCancellationReason('');
        // Notify parent to refresh
        if (onStatusUpdate) onStatusUpdate();
      } else {
        setStatusMessage({ type: 'error', text: response.data.message || 'Failed to update status' });
      }
    } catch (error) {
      const msg = error.response?.data?.message || 'Failed to update booking status';
      setStatusMessage({ type: 'error', text: msg });
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
      <div className="bg-white rounded-3xl shadow-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold">Appointment Details</h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <FiX size={24} />
          </button>
        </div>

        <div className="border-t border-gray-200 pt-4">
          <div className="flex flex-wrap -mx-2">
            <div className="w-full md:w-1/2 px-2 mb-4">
              <h4 className="text-sm font-medium text-gray-500">Customer</h4>
              <p className="text-base font-medium">{appointment.userId?.name || 'Not available'}</p>
              <p className="text-sm text-gray-500">{appointment.userId?.phoneNumber || 'No phone'}</p>
            </div>
            <div className="w-full md:w-1/2 px-2 mb-4">
              <h4 className="text-sm font-medium text-gray-500">Vendor</h4>
              <p className="text-base font-medium">{appointment.vendorId?.store_name || appointment.vendorId?.name || 'Not available'}</p>
              <p className="text-sm text-gray-500">{appointment.vendorId?.phone || 'No phone'}</p>
            </div>
          </div>

          <div className="flex flex-wrap -mx-2">
            <div className="w-full md:w-1/3 px-2 mb-4">
              <h4 className="text-sm font-medium text-gray-500">Date</h4>
              <p className="text-base font-medium">{moment(appointment.bookingDate).format('ddd, MMM D, YYYY')}</p>
            </div>
            <div className="w-full md:w-1/3 px-2 mb-4">
              <h4 className="text-sm font-medium text-gray-500">Time</h4>
              <p className="text-base font-medium">{formatTime(appointment.timeSlot)}</p>
            </div>
            <div className="w-full md:w-1/3 px-2 mb-4">
              <h4 className="text-sm font-medium text-gray-500">Status</h4>
              <div className="mt-1">
                <StatusBadge status={appointment.status} />
              </div>
            </div>
          </div>

          <div className="flex flex-wrap -mx-2">
            <div className="w-full md:w-1/2 px-2 mb-4">
              <h4 className="text-sm font-medium text-gray-500">Payment Method</h4>
              <p className="text-base font-medium capitalize">{appointment.paymentMethod}</p>
            </div>
            <div className="w-full md:w-1/2 px-2 mb-4">
              <h4 className="text-sm font-medium text-gray-500">Payment Status</h4>
              <div className="mt-1">
                <PaymentBadge status={appointment.paymentStatus} />
              </div>
            </div>
          </div>

          {/* Status Update Actions */}
          {canUpdateStatus && (
            <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <h4 className="text-sm font-medium text-gray-700 mb-3">Update Status</h4>

              {statusMessage && (
                <div className={`mb-3 p-2 rounded text-sm ${
                  statusMessage.type === 'success'
                    ? 'bg-green-50 text-green-700 border border-green-200'
                    : 'bg-red-50 text-red-700 border border-red-200'
                }`}>
                  {statusMessage.text}
                </div>
              )}

              <div className="flex flex-wrap gap-2">
                {appointment.status !== 'confirmed' && (
                  <button
                    onClick={() => handleStatusUpdate('confirmed')}
                    disabled={updating}
                    className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {updating ? 'Updating...' : 'Confirm'}
                  </button>
                )}
                <button
                  onClick={() => handleStatusUpdate('completed')}
                  disabled={updating}
                  className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {updating ? 'Updating...' : 'Complete'}
                </button>
                {!showCancelInput ? (
                  <button
                    onClick={() => setShowCancelInput(true)}
                    disabled={updating}
                    className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel Booking
                  </button>
                ) : (
                  <div className="w-full mt-2">
                    <textarea
                      value={cancellationReason}
                      onChange={(e) => setCancellationReason(e.target.value)}
                      placeholder="Enter cancellation reason..."
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-red-500 mb-2"
                      rows={2}
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleStatusUpdate('cancelled')}
                        disabled={updating}
                        className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {updating ? 'Cancelling...' : 'Confirm Cancellation'}
                      </button>
                      <button
                        onClick={() => { setShowCancelInput(false); setCancellationReason(''); }}
                        className="px-4 py-2 bg-gray-200 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-300"
                      >
                        Back
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="mb-4">
            <h4 className="text-sm font-medium text-gray-500 mb-2">Services</h4>
            {appointment.selectedServices && appointment.selectedServices.length > 0 ? (
              <div className="bg-gray-50 rounded-md p-3">
                {appointment.selectedServices.map((service, index) => (
                  <div key={index} className="flex justify-between py-2 border-b border-gray-200 last:border-0">
                    <div>
                      <p className="font-medium">{service.name}</p>
                      <p className="text-sm text-gray-500">Duration: {service.duration}</p>
                    </div>
                    <p className="font-medium">₹{service.price}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 italic">No services selected</p>
            )}
          </div>

          {appointment.selectedPackages && appointment.selectedPackages.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-medium text-gray-500 mb-2">Packages</h4>
              <div className="bg-gray-50 rounded-md p-3">
                {appointment.selectedPackages.map((pkg, index) => (
                  <div key={index} className="flex justify-between py-2 border-b border-gray-200 last:border-0">
                    <div>
                      <p className="font-medium">{pkg.name}</p>
                      <p className="text-sm text-gray-500">Duration: {pkg.duration}</p>
                    </div>
                    <p className="font-medium">₹{pkg.price}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {appointment.selectedAddons && appointment.selectedAddons.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-medium text-gray-500 mb-2">Add-ons</h4>
              <div className="bg-gray-50 rounded-md p-3">
                {appointment.selectedAddons.map((addon, index) => (
                  <div key={index} className="flex justify-between py-2 border-b border-gray-200 last:border-0">
                    <div>
                      <p className="font-medium">{addon.name}</p>
                      {addon.paidWithSubsidy && (
                        <p className="text-sm text-green-600">Paid with membership subsidy</p>
                      )}
                    </div>
                    <p className="font-medium">₹{addon.price}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          <PaymentBreakup appointment={appointment} />

          {appointment.notes && (
            <div className="mt-4 pt-3 border-t border-gray-200">
              <h4 className="text-sm font-medium text-gray-500 mb-1">Notes</h4>
              <p className="text-gray-700">{appointment.notes}</p>
            </div>
          )}

          {appointment.cancellationReason && (
            <div className="mt-4 pt-3 border-t border-gray-200">
              <h4 className="text-sm font-medium text-gray-500 mb-1">Cancellation Reason</h4>
              <p className="text-red-600">{appointment.cancellationReason}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const Appointments = () => {
  const { currentColor } = useStateContext();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('list'); // 'list', 'calendar', 'day', 'week', 'month'
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    startDate: moment().startOf('week').toDate(),
    endDate: moment().endOf('week').toDate(),
    status: '',
    paymentStatus: '',
    vendorId: '',
    searchTerm: '',
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });
  const [vendors, setVendors] = useState([]);

  // Get token from localStorage
  const token = localStorage.getItem('token');
  const headers = {
    'Authorization': `Bearer ${token}`
  };

  // Fetch vendors for filter dropdown
  const fetchVendors = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/admin/vendor/list?limit=100`,
        { headers }
      );
      
      if (response.data.success) {
        setVendors(response.data.data.vendors);
      }
    } catch (error) {
      console.error('Error fetching vendors:', error);
    }
  };

  // Fetch appointments with filters
  const fetchAppointments = async () => {
    try {
      setLoading(true);
      
      // Build query string from filters
      const queryParams = new URLSearchParams();
      queryParams.append('page', pagination.page);
      queryParams.append('limit', pagination.limit);
      
      if (filters.startDate) {
        queryParams.append('startDate', moment(filters.startDate).format('YYYY-MM-DD'));
      }
      
      if (filters.endDate) {
        queryParams.append('endDate', moment(filters.endDate).format('YYYY-MM-DD'));
      }
      
      if (filters.status) {
        queryParams.append('status', filters.status);
      }
      
      if (filters.paymentStatus) {
        queryParams.append('paymentStatus', filters.paymentStatus);
      }
      
      if (filters.vendorId) {
        queryParams.append('vendorId', filters.vendorId);
      }
      
      if (filters.searchTerm) {
        queryParams.append('search', filters.searchTerm);
      }
      
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/admin/appointments?${queryParams.toString()}`,
        { headers }
      );
      
      if (response.data.success) {
        setAppointments(response.data.data.appointments);
        setPagination({
          page: response.data.data.pagination.currentPage,
          limit: response.data.data.pagination.limit,
          total: response.data.data.pagination.totalAppointments,
          totalPages: response.data.data.pagination.totalPages,
        });
      }
    } catch (error) {
      console.error('Error fetching appointments:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle filters change
  const handleFilterChange = (name, value) => {
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Apply filters
  const applyFilters = () => {
    setPagination(prev => ({
      ...prev,
      page: 1 // Reset to first page when applying new filters
    }));
    fetchAppointments();
    setShowFilters(false);
  };

  // Reset filters
  const resetFilters = () => {
    setFilters({
      startDate: moment().startOf('week').toDate(),
      endDate: moment().endOf('week').toDate(),
      status: '',
      paymentStatus: '',
      vendorId: '',
      searchTerm: '',
    });
  };

  // Handle pagination
  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= pagination.totalPages) {
      setPagination(prev => ({
        ...prev,
        page: newPage
      }));
    }
  };

  // Format appointments for calendar view
  const formatAppointmentsForCalendar = () => {
    return appointments.map(appointment => {
      const startTime = moment(`${appointment.bookingDate} ${appointment.timeSlot}`, ['YYYY-MM-DD h:mm A', 'YYYY-MM-DD hh:mm A', 'YYYY-MM-DD HH:mm'], true);
      
      // Estimate end time based on services/packages duration
      let totalMinutes = 0;
      if (appointment.selectedServices && appointment.selectedServices.length > 0) {
        appointment.selectedServices.forEach(service => {
          // Parse duration (assuming format like "30m" or "1h")
          const durationStr = service.duration;
          if (durationStr.includes('h')) {
            totalMinutes += parseInt(durationStr) * 60;
          } else if (durationStr.includes('m')) {
            totalMinutes += parseInt(durationStr);
          } else {
            totalMinutes += 30; // Default 30 min if can't parse
          }
        });
      }
      
      if (appointment.selectedPackages && appointment.selectedPackages.length > 0) {
        appointment.selectedPackages.forEach(pkg => {
          const durationStr = pkg.duration;
          if (durationStr.includes('h')) {
            totalMinutes += parseInt(durationStr) * 60;
          } else if (durationStr.includes('m')) {
            totalMinutes += parseInt(durationStr);
          } else {
            totalMinutes += 60; // Default 60 min if can't parse
          }
        });
      }
      
      // If no services or packages, default to 30 minutes
      if (totalMinutes === 0) {
        totalMinutes = 30;
      }
      
      const endTime = moment(startTime).add(totalMinutes, 'minutes');
      
      return {
        id: appointment._id,
        title: `${appointment.userId?.name || 'Customer'} - ${appointment.vendorId?.store_name || appointment.vendorId?.name || 'Vendor'}`,
        start: startTime.toDate(),
        end: endTime.toDate(),
        resource: appointment,
        allDay: false,
      };
    });
  };

  // Calendar event renderer
  const eventStyleGetter = (event) => {
    const status = event.resource.status;
    let backgroundColor = '#3174ad'; // Default blue for confirmed
    
    switch (status) {
      case 'confirmed':
        backgroundColor = '#3174ad'; // Blue
        break;
      case 'completed':
        backgroundColor = '#5cb85c'; // Green
        break;
      case 'cancelled':
        backgroundColor = '#d9534f'; // Red
        break;
      case 'pending':
        backgroundColor = '#f0ad4e'; // Yellow
        break;
      default:
        backgroundColor = '#3174ad'; // Default blue
    }
    
    return {
      style: {
        backgroundColor,
        borderRadius: '4px',
        color: 'white',
        border: 'none',
        fontSize: '12px',
        padding: '2px 4px',
        opacity: 0.9,
        display: 'block',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }
    };
  };

  // Set up date navigation for calendar
  const navigateCalendar = (action) => {
    let newDate;
    if (viewMode === 'day') {
      newDate = action === 'PREV' 
        ? moment(selectedDate).subtract(1, 'day').toDate()
        : moment(selectedDate).add(1, 'day').toDate();
    } else if (viewMode === 'week') {
      newDate = action === 'PREV' 
        ? moment(selectedDate).subtract(1, 'week').toDate()
        : moment(selectedDate).add(1, 'week').toDate();
    } else if (viewMode === 'month') {
      newDate = action === 'PREV' 
        ? moment(selectedDate).subtract(1, 'month').toDate()
        : moment(selectedDate).add(1, 'month').toDate();
    }
    
    setSelectedDate(newDate);
    
    // Update filters based on the selected view and date
    if (viewMode === 'day') {
      setFilters(prev => ({
        ...prev,
        startDate: moment(newDate).startOf('day').toDate(),
        endDate: moment(newDate).endOf('day').toDate(),
      }));
    } else if (viewMode === 'week') {
      setFilters(prev => ({
        ...prev,
        startDate: moment(newDate).startOf('week').toDate(),
        endDate: moment(newDate).endOf('week').toDate(),
      }));
    } else if (viewMode === 'month') {
      setFilters(prev => ({
        ...prev,
        startDate: moment(newDate).startOf('month').toDate(),
        endDate: moment(newDate).endOf('month').toDate(),
      }));
    }
  };

  // Change view mode and update date range
  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    
    if (mode === 'day') {
      setFilters(prev => ({
        ...prev,
        startDate: moment(selectedDate).startOf('day').toDate(),
        endDate: moment(selectedDate).endOf('day').toDate(),
      }));
    } else if (mode === 'week') {
      setFilters(prev => ({
        ...prev,
        startDate: moment(selectedDate).startOf('week').toDate(),
        endDate: moment(selectedDate).endOf('week').toDate(),
      }));
    } else if (mode === 'month') {
      setFilters(prev => ({
        ...prev,
        startDate: moment(selectedDate).startOf('month').toDate(),
        endDate: moment(selectedDate).endOf('month').toDate(),
      }));
    }
  };

  // Format date range for display
  const getDateRangeText = () => {
    if (viewMode === 'day') {
      return moment(selectedDate).format('MMMM D, YYYY');
    } else if (viewMode === 'week') {
      const startOfWeek = moment(selectedDate).startOf('week');
      const endOfWeek = moment(selectedDate).endOf('week');
      return `${startOfWeek.format('MMM D')} - ${endOfWeek.format('MMM D, YYYY')}`;
    } else if (viewMode === 'month') {
      return moment(selectedDate).format('MMMM YYYY');
    }
    return '';
  };

  // Initialize component - fetch vendors and appointments
  useEffect(() => {
    fetchVendors();
  }, []);

  // Fetch appointments when filters or pagination changes
  useEffect(() => {
    fetchAppointments();
  }, [pagination.page, pagination.limit, viewMode, selectedDate]);

  return (
    <div className="card p-5 md:p-8">
      <Header category="Management" title="Appointments" />
      
      {/* View mode tabs and date navigation */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
        <div className="flex mb-4 sm:mb-0">
          <button
            onClick={() => handleViewModeChange('list')}
            className={`px-4 py-2 rounded-l-md text-sm font-medium ${
              viewMode === 'list' 
                ? 'bg-blue-50 text-blue-700 border border-blue-200' 
                : 'bg-gray-50 text-gray-700 border border-gray-200 hover:bg-gray-100'
            }`}
          >
            List View
          </button>
          <button
            onClick={() => handleViewModeChange('day')}
            className={`px-4 py-2 text-sm font-medium border-t border-b ${
              viewMode === 'day' 
                ? 'bg-blue-50 text-blue-700 border-blue-200' 
                : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100'
            }`}
          >
            Day
          </button>
          <button
            onClick={() => handleViewModeChange('week')}
            className={`px-4 py-2 text-sm font-medium border-t border-b ${
              viewMode === 'week' 
                ? 'bg-blue-50 text-blue-700 border-blue-200' 
                : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100'
            }`}
          >
            Week
          </button>
          <button
            onClick={() => handleViewModeChange('month')}
            className={`px-4 py-2 rounded-r-md text-sm font-medium ${
              viewMode === 'month' 
                ? 'bg-blue-50 text-blue-700 border border-blue-200' 
                : 'bg-gray-50 text-gray-700 border border-gray-200 hover:bg-gray-100'
            }`}
          >
            Month
          </button>
        </div>
        
        {viewMode !== 'list' && (
          <div className="flex items-center">
            <button
              onClick={() => navigateCalendar('PREV')}
              className="p-2 rounded-full hover:bg-gray-100"
            >
              <FiChevronLeft />
            </button>
            <div className="mx-4 font-medium">
              {getDateRangeText()}
            </div>
            <button
              onClick={() => navigateCalendar('NEXT')}
              className="p-2 rounded-full hover:bg-gray-100"
            >
              <FiChevronRight />
            </button>
          </div>
        )}
      </div>
      
      {/* Search and filter controls */}
      <div className="flex flex-col md:flex-row justify-between mb-6">
        <div className="flex mb-4 md:mb-0">
          <div className="relative">
            <input
              type="text"
              placeholder="Search by customer name or phone"
              className="w-full md:w-64 border border-gray-300 rounded-md pl-10 pr-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500"
              value={filters.searchTerm}
              onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && applyFilters()}
            />
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="ml-2 px-3 py-2 border border-gray-300 rounded-md flex items-center hover:bg-gray-50"
          >
            <FiFilter className="mr-1" />
            Filters
          </button>
        </div>
      </div>
      
      {/* Filter panel */}
      {showFilters && (
        <div className="bg-gray-50 p-4 rounded-md mb-6 border border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500"
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <option value="">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="confirmed">Confirmed</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Payment Status</label>
              <select
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500"
                value={filters.paymentStatus}
                onChange={(e) => handleFilterChange('paymentStatus', e.target.value)}
              >
                <option value="">All Payment Statuses</option>
                <option value="pending">Pending</option>
                <option value="paid">Paid</option>
                <option value="refunded">Refunded</option>
                <option value="failed">Failed</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Vendor</label>
              <select
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500"
                value={filters.vendorId}
                onChange={(e) => handleFilterChange('vendorId', e.target.value)}
              >
                <option value="">All Vendors</option>
                {vendors.map(vendor => (
                  <option key={vendor._id} value={vendor._id}>
                    {vendor.store_name || vendor.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
              <div className="flex space-x-2">
                <DatePicker
                  selected={filters.startDate}
                  onChange={(date) => handleFilterChange('startDate', date)}
                  selectsStart
                  startDate={filters.startDate}
                  endDate={filters.endDate}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  dateFormat="MMM d, yyyy"
                />
                <DatePicker
                  selected={filters.endDate}
                  onChange={(date) => handleFilterChange('endDate', date)}
                  selectsEnd
                  startDate={filters.startDate}
                  endDate={filters.endDate}
                  minDate={filters.startDate}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  dateFormat="MMM d, yyyy"
                />
              </div>
            </div>
          </div>
          <div className="mt-4 flex justify-end space-x-2">
            <button
              onClick={resetFilters}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Reset
            </button>
            <button
              onClick={applyFilters}
              className="px-4 py-2 rounded-md text-white"
              style={{ backgroundColor: currentColor }}
            >
              Apply Filters
            </button>
          </div>
        </div>
      )}
      
      {/* Loading indicator */}
      {loading && (
        <div className="flex justify-center items-center my-12">
          <div className="spinner-border animate-spin inline-block w-8 h-8 border-4 rounded-full" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}
      
      {/* Calendar view */}
      {!loading && viewMode !== 'list' && (
        <div className="mt-6" style={{ height: 600 }}>
          <Calendar
            localizer={localizer}
            events={formatAppointmentsForCalendar()}
            startAccessor="start"
            endAccessor="end"
            style={{ height: '100%' }}
            views={{
              day: true,
              week: true,
              month: true,
            }}
            view={viewMode}
            date={selectedDate}
            onNavigate={(date) => setSelectedDate(date)}
            eventPropGetter={eventStyleGetter}
            onSelectEvent={(event) => setSelectedAppointment(event.resource)}
            dayPropGetter={(date) => ({
              style: {
                backgroundColor: moment(date).isSame(moment(), 'day') ? '#f9fafb' : 'inherit',
              },
            })}
          />
        </div>
      )}
      
      {/* List view */}
      {!loading && viewMode === 'list' && (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Customer
                  </th>                  
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Vendor
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date & Time
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Services
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Payment
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {appointments.length > 0 ? (
                  appointments.map((appointment) => (
                    <tr key={appointment._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 bg-gray-200 rounded-full flex items-center justify-center">
                            <FiUser className="text-gray-500" />
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {appointment.userId?.name || 'Not available'}
                            </div>
                            <div className="text-sm text-gray-500">
                              {appointment.userId?.phoneNumber || 'No phone'}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {appointment.vendorId?.store_name || appointment.vendorId?.name || 'Not available'}
                        </div>
                        <div className="text-sm text-gray-500 flex items-center">
                          <FiMapPin className="mr-1" size={12} />
                          {appointment.vendorId?.vendorType === 'home' ? 'Home Service' : 'In-store'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {moment(appointment.bookingDate).format('MMM D, YYYY')}
                        </div>
                        <div className="text-sm text-gray-500 flex items-center">
                          <FiClock className="mr-1" size={12} />
                          {moment(appointment.timeSlot, ['h:mm A', 'hh:mm A', 'HH:mm'], true).format('h:mm A')}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          {appointment.selectedServices && appointment.selectedServices.length > 0 && (
                            <div className="mb-1">
                              {appointment.selectedServices.slice(0, 2).map((service, index) => (
                                <div key={index} className="mb-1 last:mb-0">
                                  {service.name}
                                </div>
                              ))}
                              {appointment.selectedServices.length > 2 && (
                                <div className="text-gray-500 text-xs">
                                  +{appointment.selectedServices.length - 2} more
                                </div>
                              )}
                            </div>
                          )}
                          {appointment.selectedPackages && appointment.selectedPackages.length > 0 && (
                            <div className="text-xs font-medium bg-gray-100 px-2 py-1 rounded-md inline-block">
                              {appointment.selectedPackages.length} Package(s)
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        {formatAmount(appointment.totalAmount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <StatusBadge status={appointment.status} />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <PaymentBadge status={appointment.paymentStatus} />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <button
                          onClick={() => setSelectedAppointment(appointment)}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" className="px-6 py-4 text-center text-gray-500">
                      No appointments found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          {appointments.length > 0 && (
            <div className="flex justify-between items-center mt-6">
              <div className="text-sm text-gray-700">
                Showing <span className="font-medium">{(pagination.page - 1) * pagination.limit + 1}</span> to{' '}
                <span className="font-medium">
                  {Math.min(pagination.page * pagination.limit, pagination.total)}
                </span>{' '}
                of <span className="font-medium">{pagination.total}</span> appointments
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  className={`px-3 py-1 border rounded-md ${
                    pagination.page === 1
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Previous
                </button>
                {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                  const pageNum = i + 1;
                  return (
                    <button
                      key={i}
                      onClick={() => handlePageChange(pageNum)}
                      className={`px-3 py-1 border rounded-md ${
                        pagination.page === pageNum
                          ? 'bg-blue-50 text-blue-600 border-blue-500'
                          : 'bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                {pagination.totalPages > 5 && (
                  <button
                    className="px-3 py-1 border rounded-md bg-white text-gray-700"
                    disabled
                  >
                    ...
                  </button>
                )}
                {pagination.totalPages > 5 && (
                  <button
                    onClick={() => handlePageChange(pagination.totalPages)}
                    className={`px-3 py-1 border rounded-md ${
                      pagination.page === pagination.totalPages
                        ? 'bg-blue-50 text-blue-600 border-blue-500'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {pagination.totalPages}
                  </button>
                )}
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.totalPages}
                  className={`px-3 py-1 border rounded-md ${
                    pagination.page === pagination.totalPages
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
      
      {/* Appointment details modal */}
      {selectedAppointment && (
        <AppointmentModal
          appointment={selectedAppointment}
          onClose={() => setSelectedAppointment(null)}
          onStatusUpdate={() => {
            fetchAppointments();
            setSelectedAppointment(null);
          }}
        />
      )}
    </div>
  );
};

export default Appointments;